/**
 * `GeometryProvider` that reads the pre-tessellated `.mmtg` binaries of the
 * map bundle this page uses — the bundled `public/map-v2/` set or a
 * downloaded one (`fetchMapAsset`, TASK-1123): four element-type
 * files per theme (connections, stations, additionals, transitions), or of
 * the late-night frame (`night`, TASK-1136: the `*-night.mmtg` files in the
 * frame's own transfer order, on the dark palette); labels are the set's
 * `texts-*.svg` overlay. No parsing or triangulation in the
 * browser (TASK-243).
 */

import type { GeometryProvider } from './GeometryProvider'
import type { ElementGeometry, GeometrySet, MapVariant, RenderFloor } from './types'
import { FLAG_SKIP_INITIAL_DRAW, MmtgElementType } from './mmtg/format'
import { readMmtg } from './mmtg/reader'
import { fetchMapAsset, getMapBundleSession } from '../mapDelivery/defaultMapBundleSession'

const FILES: ReadonlyArray<readonly [MmtgElementType, string]> = [
  [MmtgElementType.Connections, 'connections'],
  [MmtgElementType.Stations, 'stations'],
  [MmtgElementType.Additionals, 'additionals'],
  [MmtgElementType.Transitions, 'transitions'],
]

export class PrecomputedMmtgProvider implements GeometryProvider {
  async load(variant: MapVariant): Promise<GeometrySet> {
    const [files, transitionOrder] = await Promise.all([
      Promise.all(
        FILES.map(async ([type, name]) => {
          const file = `${name}-${variant}.mmtg`
          const res = await fetchMapAsset(file)
          if (!res.ok) {
            throw new Error(`PrecomputedMmtgProvider: fetch ${file} failed: HTTP ${res.status}`)
          }
          const buf = await res.arrayBuffer()
          try {
            return readMmtg(buf, type)
          } catch (err: unknown) {
            // Unreadable geometry breaks the bundle (spec §3.6): mark it, fall back.
            void getMapBundleSession().reportBroken()
            throw err
          }
        }),
      ),
      fetchTransitionOrder(
        variant === 'night' ? 'transitions-order-night.json' : 'transitions-order.json',
      ),
    ])

    const elements: ElementGeometry[] = []
    for (const file of files) {
      // Transitions need re-ordering to restore Figma z-order (MMTG id-sorts
      // entries, which loses the schema's reverse z-order).
      // Lower schema index = higher z (front-most) — painted last.
      const entries =
        file.elementType === MmtgElementType.Transitions
          ? [...file.entries].sort((a, b) => {
              const ao = transitionOrder.get(a.id) ?? Number.MAX_SAFE_INTEGER
              const bo = transitionOrder.get(b.id) ?? Number.MAX_SAFE_INTEGER
              // Higher index first → lower index last → front-most on top (painter's)
              return bo - ao
            })
          : file.entries
      for (const entry of entries) {
        if ((entry.flags & FLAG_SKIP_INITIAL_DRAW) !== 0) continue
        elements.push({
          id: entry.id,
          fillVertices: entry.fillVertices,
          strokeVertices: entry.strokeVertices,
          renderFloor: entry.renderFloor as RenderFloor,
        })
      }
    }
    // Painter's algorithm — stable sort preserves transitions' reorder above.
    elements.sort((a, b) => a.renderFloor - b.renderFloor)

    // The night frame is drawn on the dark palette.
    return { version: '1', theme: variant === 'night' ? 'dark' : variant, elements }
  }
}

async function fetchTransitionOrder(file: string): Promise<Map<string, number>> {
  try {
    const res = await fetchMapAsset(file)
    if (!res.ok) return new Map()
    const ids: string[] = await res.json()
    const m = new Map<string, number>()
    ids.forEach((id, idx) => m.set(id, idx))
    return m
  } catch {
    // Non-fatal — without the order file transitions default to id-sort.
    return new Map()
  }
}
